import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, formatPrice, type CurrentUser, type EstablishedLink } from '../lib/api';
import { UnlinkedQueue } from './UnlinkedQueue';
import { ProductPicker } from './ProductPicker';

/**
 * Link management: which product each marketplace listing points at.
 *
 * Three views of one relationship. A listing has no product yet (the queue), it
 * has one that may be wrong (the audit), or a candidate for it was rejected and
 * needs to come back (dismissals). Creating a link already had a home; the
 * other two had none — an established link could only be reached by expanding
 * its product row on the Products tab, one at a time, which is not an audit
 * anybody performs 331 times.
 *
 * Price and title editing deliberately stay on the Products tab. This tab owns
 * exactly one thing.
 */

type Section = 'queue' | 'linked' | 'dismissed';

export function LinkManager({ user }: { user: CurrentUser }) {
  const [section, setSection] = useState<Section>('queue');

  // Counts sit on the switcher so the shape of the work is visible before
  // choosing a section. Each is the same query its section uses, so switching
  // renders from cache rather than fetching again.
  const queue = useQuery({ queryKey: ['unlinked', false], queryFn: () => api.unlinked(false) });
  const linked = useQuery({ queryKey: ['links', 'judgement'], queryFn: () => api.links('judgement') });
  const dismissed = useQuery({ queryKey: ['dismissed'], queryFn: api.dismissedSuggestions });

  const flagged = (linked.data?.links ?? []).filter((link) => link.disagreements.length > 0).length;

  return (
    <div>
      <div className="card section-switch">
        <button className={section === 'queue' ? 'active' : ''} onClick={() => setSection('queue')}>
          Needs a link
          {queue.data && <span className="pill">{queue.data.total}</span>}
        </button>
        <button className={section === 'linked' ? 'active' : ''} onClick={() => setSection('linked')}>
          Linked
          {linked.data && <span className="pill">{linked.data.total}</span>}
          {/* Surfaced on the switcher because a contradiction nobody looks at is
              the same as no audit at all. */}
          {flagged > 0 && <span className="pill warn">{flagged} to check</span>}
        </button>
        <button
          className={section === 'dismissed' ? 'active' : ''}
          onClick={() => setSection('dismissed')}
        >
          Dismissed
          {dismissed.data && <span className="pill">{dismissed.data.total}</span>}
        </button>
      </div>

      {section === 'queue' && <UnlinkedQueue user={user} />}
      {section === 'linked' && <LinkedList user={user} />}
      {section === 'dismissed' && <DismissedList user={user} />}
    </div>
  );
}

/** How far a marketplace price sits above or below the website price. */
function priceGap(link: EstablishedLink): string | null {
  const site = link.product?.sitePriceMinor;
  if (link.priceMinor === null || site === null || site === undefined || site === 0) return null;
  const percent = ((link.priceMinor - site) / site) * 100;
  if (Math.abs(percent) < 0.5) return 'same';
  return `${percent > 0 ? '+' : ''}${percent.toFixed(1)}%`;
}

function LinkedList({ user }: { user: CurrentUser }) {
  const queryClient = useQueryClient();
  const canEdit = user.role === 'admin' || user.role === 'editor';
  const [toast, setToast] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [basis, setBasis] = useState<'judgement' | 'all'>('judgement');
  const [channelId, setChannelId] = useState('');
  const [changing, setChanging] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['links', basis],
    queryFn: () => api.links(basis),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['links'] });
    void queryClient.invalidateQueries({ queryKey: ['unlinked'] });
    void queryClient.invalidateQueries({ queryKey: ['products'] });
  };

  const relink = useMutation({
    mutationFn: ({ listingId, productId }: { listingId: string; productId: string }) =>
      api.link(listingId, productId),
    onSuccess: () => {
      setChanging(null);
      setToast({ kind: 'ok', text: 'Link changed' });
      refresh();
    },
    onError: (err: Error) => setToast({ kind: 'error', text: err.message }),
  });

  const remove = useMutation({
    mutationFn: (listingId: string) => api.unlink(listingId),
    onSuccess: () => {
      setToast({ kind: 'ok', text: 'Unlinked. The listing is back in the queue.' });
      refresh();
    },
    onError: (err: Error) => setToast({ kind: 'error', text: err.message }),
  });

  if (isLoading) return <div className="empty-state">Loading…</div>;
  if (error) return <div className="alert error">{(error as Error).message}</div>;

  const all = data?.links ?? [];
  const channels = [...new Map(all.map((link) => [link.channelId, link.channelName])).entries()];

  // Anything contradictory first, since that is the only reason to read this
  // list top to bottom rather than search it.
  const links = all
    .filter((link) => channelId === '' || link.channelId === channelId)
    .sort((a, b) => {
      const flag = Number(b.disagreements.length > 0) - Number(a.disagreements.length > 0);
      if (flag !== 0) return flag;
      return a.channelName.localeCompare(b.channelName) || a.title.localeCompare(b.title);
    });

  const flagged = links.filter((link) => link.disagreements.length > 0).length;
  const skuCount = (data?.total ?? 0) - (data?.judgementCount ?? 0);

  return (
    <div>
      <div className="toolbar">
        <strong>{links.length}</strong>
        <span className="muted">
          established link{links.length === 1 ? '' : 's'}
          {basis === 'judgement' ? ' a person decided' : ''}
        </span>
        {channels.length > 1 && (
          <select value={channelId} onChange={(event) => setChannelId(event.target.value)}>
            <option value="">All channels</option>
            {channels.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        )}
        <div className="spacer" />
        {/* An exact SKU match had no room to be wrong, so those sit behind a
            toggle rather than three hundred rows with nothing to review. */}
        {basis === 'judgement' ? (
          <span className="small muted">
            {skuCount} exact SKU match{skuCount === 1 ? '' : 'es'} hidden{' · '}
            <button className="link small" onClick={() => setBasis('all')}>
              show them
            </button>
          </span>
        ) : (
          <button className="link small" onClick={() => setBasis('judgement')}>
            hide exact SKU matches
          </button>
        )}
      </div>

      {toast && (
        <div className={`alert ${toast.kind}`} onClick={() => setToast(null)}>
          {toast.text}
        </div>
      )}

      {flagged > 0 && (
        <div className="alert warn">
          {flagged} link{flagged === 1 ? '' : 's'} contradict{flagged === 1 ? 's' : ''} itself on
          type, size, drawer count or finish — shown first below.
        </div>
      )}

      {links.length === 0 ? (
        <div className="empty-state">
          <p>No links to show.</p>
        </div>
      ) : (
        links.map((link) => (
          <LinkRow
            key={link.id}
            link={link}
            canEdit={canEdit}
            changing={changing === link.id}
            busy={relink.isPending || remove.isPending}
            onToggleChange={() => setChanging((current) => (current === link.id ? null : link.id))}
            onChoose={(productId) => relink.mutate({ listingId: link.id, productId })}
            onUnlink={() => {
              if (!window.confirm(`Unlink "${link.title}" from ${link.product?.title}?`)) return;
              remove.mutate(link.id);
            }}
          />
        ))
      )}
    </div>
  );
}

function LinkRow({
  link,
  canEdit,
  changing,
  busy,
  onToggleChange,
  onChoose,
  onUnlink,
}: {
  link: EstablishedLink;
  canEdit: boolean;
  changing: boolean;
  busy: boolean;
  onToggleChange: () => void;
  onChoose: (productId: string) => void;
  onUnlink: () => void;
}) {
  const gap = priceGap(link);

  return (
    <div className={link.disagreements.length > 0 ? 'link-row flagged' : 'link-row'}>
      <div className="link-pair">
        <div className="link-side">
          {link.imageUrl ? (
            <img className="thumb" src={link.imageUrl} alt="" loading="lazy" />
          ) : (
            <div className="thumb" />
          )}
          <div>
            <div>
              <span className="badge">{link.channelName}</span>{' '}
              {link.linkType && <span className="badge">{link.linkType} link</span>}{' '}
              {link.status === 'inactive' && (
                <span
                  className="badge inactive"
                  title="The channel is not currently showing this listing"
                >
                  inactive
                </span>
              )}
            </div>
            <div>{link.title}</div>
            <div className="small muted">
              SKU <span className="sku">{link.sku ?? 'none'}</span> ·{' '}
              {formatPrice(link.priceMinor, link.currency)}
              {link.url && (
                <>
                  {' · '}
                  <a href={link.url} target="_blank" rel="noreferrer">
                    view ↗
                  </a>
                </>
              )}
            </div>
          </div>
        </div>

        {/* The gap against the website price. This is what exposed the one wrong
            link on the live catalog: the correct ones all sat a few percent
            above the site, and that one sat below, because it was being
            compared against a dearer finish. */}
        <div className="link-join">
          <span className="arrow">→</span>
          {gap && <span className="small muted">{gap}</span>}
        </div>

        <div className="link-side">
          {link.product?.imageUrl ? (
            <img className="thumb" src={link.product.imageUrl} alt="" loading="lazy" />
          ) : (
            <div className="thumb" />
          )}
          <div>
            <div>
              <span className="badge">{link.product?.siteName ?? 'website'}</span>
            </div>
            <div>{link.product?.title}</div>
            <div className="small muted">
              SKU <span className="sku">{link.product?.skuDisplay}</span>
              {link.product?.sitePriceMinor !== null && (
                <>
                  {' · '}
                  {formatPrice(
                    link.product?.sitePriceMinor ?? null,
                    link.product?.siteCurrency ?? 'USD',
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {canEdit && (
          <div className="link-actions">
            {/* Re-pointing rather than unlink-then-relink: unlinking drops the
                listing into the queue, and a distraction mid-way loses the
                association and gains a queue item. */}
            <button disabled={busy} onClick={onToggleChange}>
              {changing ? 'Cancel' : 'Change link…'}
            </button>
            <button className="link small" disabled={busy} onClick={onUnlink}>
              Unlink
            </button>
          </div>
        )}
      </div>

      {link.disagreements.length > 0 && (
        <ul className="link-flags">
          {link.disagreements.map((note, index) => (
            <li key={index}>{note}</li>
          ))}
        </ul>
      )}

      {changing && (
        <ProductPicker listingId={link.id} actionLabel="Use this" busy={busy} onChoose={onChoose} />
      )}
    </div>
  );
}

/**
 * Rejected suggestions, with a way back.
 *
 * Dismissal is permanent by design — it is what stops the queue re-proposing a
 * wrong candidate after every sync — so a mis-click otherwise suppresses the
 * right answer for good, and nothing in the UI would ever mention it again.
 */
function DismissedList({ user }: { user: CurrentUser }) {
  const queryClient = useQueryClient();
  const canEdit = user.role === 'admin' || user.role === 'editor';
  const [toast, setToast] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['dismissed'],
    queryFn: api.dismissedSuggestions,
  });

  const restore = useMutation({
    mutationFn: ({ listingId, productId }: { listingId: string; productId: string }) =>
      api.restoreSuggestion(listingId, productId),
    onSuccess: () => {
      setToast({ kind: 'ok', text: 'Restored. It is a candidate again.' });
      void queryClient.invalidateQueries({ queryKey: ['dismissed'] });
      void queryClient.invalidateQueries({ queryKey: ['unlinked'] });
    },
    onError: (err: Error) => setToast({ kind: 'error', text: err.message }),
  });

  if (isLoading) return <div className="empty-state">Loading…</div>;
  if (error) return <div className="alert error">{(error as Error).message}</div>;

  const rows = data?.dismissed ?? [];

  return (
    <div>
      <div className="toolbar">
        <strong>{rows.length}</strong>
        <span className="muted">rejected suggestion{rows.length === 1 ? '' : 's'}</span>
      </div>

      {toast && (
        <div className={`alert ${toast.kind}`} onClick={() => setToast(null)}>
          {toast.text}
        </div>
      )}

      {rows.length === 0 ? (
        <div className="empty-state">
          <p>Nothing has been rejected.</p>
          <p className="small">
            Suggestions marked “Not this” in the queue appear here, so a mis-click can be undone.
          </p>
        </div>
      ) : (
        rows.map((row) => (
          <div className="queue-item" key={`${row.listingId}:${row.productId}`}>
            <div className="small muted">
              <span className="badge">{row.channelName}</span> {row.listingTitle} ·{' '}
              <span className="sku">{row.listingSku ?? 'none'}</span>
            </div>
            <div>
              <span className="score">{row.score}%</span>{' '}
              <span className="sku">{row.productSku}</span> — {row.productTitle}
            </div>
            <div className="small muted" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {/* Restoring a candidate for a listing that has since been linked
                  changes nothing visible, so it says so rather than seeming
                  broken. */}
              {row.listingAlreadyLinked && <span>This listing is already linked to a product.</span>}
              {canEdit && (
                <button
                  disabled={restore.isPending}
                  onClick={() =>
                    restore.mutate({ listingId: row.listingId, productId: row.productId })
                  }
                >
                  Restore
                </button>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
